import { useOrganizationContext } from "@src/modules/member/context/organization-context/hook";
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BackendResponseFormat,
  MutationDefaultOptions,
  backendFetch,
} from "@utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useLanguage } from "../../../hooks/helperHooks";
import { Feature, FeatureSlug, Organization } from "../models/organization";

export const queryKeys = {
  organizations: "ORGANIZATIONS",
};

type UseOrganizationsResult = UseQueryResult<
  BackendResponseFormat<Organization[]>
>;
export function useOrganizations(): UseOrganizationsResult {
  const language = useLanguage();

  return useQuery({
    queryKey: [queryKeys.organizations, "ALL"],
    queryFn: async () => {
      const response = await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/organizations",
        language,
      });

      return response;
    },
  });
}

interface CreateOrganizationRequest {
  name: string;
  country: string;
  state: string;
  zip?: string;
  city?: string;
  address?: string;
  address2?: string;
}
type UseCreateOrganizationResult = UseMutationResult<
  BackendResponseFormat<Organization> | string,
  Error,
  CreateOrganizationRequest,
  unknown
>;
export function useCreateOrganization(
  mutationOptions: MutationDefaultOptions,
): UseCreateOrganizationResult {
  const language = useLanguage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: CreateOrganizationRequest,
    ): Promise<BackendResponseFormat<Organization> | string> => {
      const response = await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/organizations",
        language,
        method: "POST",
        body: data,
      });

      return response as { data: Organization };
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.organizations, "ALL"],
      });
    },
  });
}

type UseOrganizationResult = UseQueryResult<
  BackendResponseFormat<Organization>
>;
export function useOrganization(code: string): UseOrganizationResult {
  const language = useLanguage();

  return useQuery({
    queryKey: [queryKeys.organizations, code],
    queryFn: async () => {
      const response = await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/api/v0/member/organizations/${code}`,
        language,
      });

      return response;
    },
    retry: 2,
    enabled: !!code,
  });
}

type UseUpdateOrganizationResult = UseMutationResult<
  BackendResponseFormat<Organization> | string,
  Error,
  {
    name?: string;
    tag_line?: string;
    email?: string;
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    settings?: {
      allow_partial_payment_for_invoices?: boolean;
      pass_on_credit_card_fee?: boolean;
      first_invoice_reminder?: string;
      second_invoice_reminder?: string;
      third_invoice_reminder?: string;
      manual_invoice_reminder?: string;
      timezone?: string;
      working_days?: number[];
      daily_prompt_time?: string;
      daily_slack_prompt_enabled?: boolean;
      rate_lock_policy?: "at_invoice" | "at_creation" | "none";
      invoice_number_start?: number;
      invoice_number_prefix?: string | null;
      receipt_number_start?: number;
      receipt_number_prefix?: string | null;
      default_invoice_terms?: string | null;
    };
  },
  unknown
>;
export function useUpdateOrganization(
  code: string | undefined,
  mutationOptions?: MutationDefaultOptions | undefined,
): UseUpdateOrganizationResult {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async (
      data,
    ): Promise<BackendResponseFormat<Organization> | string> => {
      if (!code) {
        throw new Error("Organization code is required");
      }

      const response = await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/api/v0/member/organizations/${code}`,
        language,
        method: "PUT",
        body: data,
      });

      return response as { data: Organization };
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions?.errorKey ?? "UPDATE_ORGANIZATION_ERROR",
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.organizations, "ALL"],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.organizations, code],
      });
    },
  });
}

export function useUploadImage(
  mutationOptions: MutationDefaultOptions,
): UseMutationResult<void, Error, FormData, unknown> {
  const queryClient = useQueryClient();
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useMutation({
    mutationFn: async (data: FormData): Promise<void> => {
      await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/upload-organization-image",
        method: "POST",
        body: data,
        multipart: true,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.organizations, currentOrganizationCode],
      });
    },
  });
}

export function useDeleteImage(
  mutationOptions: MutationDefaultOptions,
): UseMutationResult<
  void,
  Error,
  { field: "organization_settings.invoice_logo_url" | "image_url" },
  unknown
> {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useMutation({
    mutationFn: async (data: {
      field: "organization_settings.invoice_logo_url" | "image_url";
    }): Promise<void> => {
      await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/delete-organization-image",
        method: "POST",
        body: data,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.organizations, currentOrganizationCode],
      });
    },
    onError: () => {
      toast.error(t("deleteImageError"));
    },
  });
}

export function useGetOrganizationListCodes(): {
  organizationCodes: string[];
  isLoading: boolean;
} {
  const { data: organizationsResponse, isLoading } = useOrganizations();
  const organizations = organizationsResponse?.data ?? [];

  return {
    organizationCodes: organizations.map((organization) => organization.code),
    isLoading,
  };
}

export function useCurrentOrganization(): Organization | undefined {
  const { currentOrganizationCode } = useOrganizationContext();
  const { data: organizationResponse, dataUpdatedAt } = useOrganization(
    currentOrganizationCode as string,
  );

  const organization = organizationResponse?.data;

  useEffect(() => {
    document.title = `Invup | ${organization?.name || ""}`;
  }, [dataUpdatedAt]);

  return organization;
}

export function useOrganizationFeatures(): Feature[] {
  const organization = useCurrentOrganization();

  const features: Feature[] = [];

  organization?.feature_packs?.data.forEach((featureFlag) => {
    featureFlag.features.data.forEach((feature) => {
      features.push(feature);
    });
  });

  organization?.direct_features?.data.forEach((directFeature) => {
    features.push(directFeature);
  });

  return features;
}

export function useOrganizationHasFeature(feature: FeatureSlug): boolean {
  const features = useOrganizationFeatures();

  return features.some((f) => f.slug === feature);
}
